import type { VariantProps } from 'class-variance-authority';
import type {
  ComponentPropsWithoutRef,
  ElementRef,
  ElementType,
  ReactNode,
  Ref,
} from 'react';

import { cnva } from '@/app/utils/cnva';

export const validSectionComponents = [
  'div',
  'article',
  'aside',
  'nav',
  'section',
  'header',
  'footer',
] satisfies ElementType[];

type SectionAs = (typeof validSectionComponents)[number];

const SectionStyled = cnva('relative w-full', {
  variants: {
    size: {
      sm: 'py-4 md:py-6 xl:py-12',
      md: 'py-7 md:py-10 xl:py-16',
      lg: 'py-10 md:py-14 xl:py-20',
      none: '',
    },
    overflow: {
      hidden: 'overflow-hidden',
      none: '',
    },
  },
  defaultVariants: {
    size: 'lg',
    overflow: 'hidden',
  },
});

type SectionProps<T extends SectionAs = 'section'> = Omit<
  ComponentPropsWithoutRef<T>,
  'children'
> &
  VariantProps<typeof SectionStyled> & {
    /**
     * Specifies the HTML tag for the Section component.
     */
    as?: T;
    /**
     * The content to be displayed within the section.
     */
    children: ReactNode;
    /**
     * Additional CSS class names to apply to the section.
     */
    className?: string;
    /**
     * A React ref to the Section component.
     */
    ref?: Ref<ElementRef<T>>;
  };

export const Section = <T extends SectionAs = 'section'>({
  as,
  children,
  className,
  size = 'lg',
  overflow = 'hidden',
  ref,
  ...rest
}: SectionProps<T>) => {
  const Component = (
    as && validSectionComponents.includes(as) ? as : 'section'
  ) as ElementType;

  return (
    <Component
      ref={ref}
      className={SectionStyled({ size, overflow, className })}
      {...rest}
    >
      {children}
    </Component>
  );
};
